import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Robot, FloppyDisk, ArrowCounterClockwise, Play, Sparkle } from "@phosphor-icons/react";

export default function KiEinstellungen() {
  const [prompt, setPrompt] = useState("");
  const [saving, setSaving] = useState(false);
  const [testName, setTestName] = useState("Story Bar Hamburg GmbH");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    const { data } = await api.get("/settings/name-prompt");
    setPrompt(data.prompt);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/name-prompt", { prompt });
      toast.success("Prompt gespeichert");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    const { data } = await api.post("/settings/name-prompt/reset");
    setPrompt(data.prompt);
    toast.success("Auf Standard zurückgesetzt");
  };

  const runTest = async () => {
    if (!testName.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/ai/derive-name", { company_name: testName, prompt });
      setTestResult(data);
    } catch (err) {
      setTestResult({ error: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl">
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold tracking-tight">KI-Namen</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Konfiguriere den KI-Prompt, der aus dem offiziellen Firmennamen den kurzen Markennamen
          (<code className="text-[#002FA7]">name_for_emails</code>) ableitet. Teste das Ergebnis live im Playground.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Prompt config */}
        <div className="bg-white border border-zinc-200 rounded-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <Robot size={18} className="text-[#002FA7]" />
            <h2 className="font-heading text-lg font-semibold">System-Prompt</h2>
          </div>
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={16}
            data-testid="prompt-textarea"
            className="font-mono text-xs leading-relaxed"
          />
          <div className="flex gap-2 mt-4">
            <Button onClick={save} disabled={saving} data-testid="save-prompt-button" className="bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm">
              <FloppyDisk size={16} className="mr-2" />
              {saving ? "Speichern…" : "Speichern"}
            </Button>
            <Button onClick={reset} variant="outline" data-testid="reset-prompt-button" className="rounded-sm border-zinc-300">
              <ArrowCounterClockwise size={16} className="mr-2" /> Standard
            </Button>
          </div>
        </div>

        {/* Playground */}
        <div className="bg-white border border-zinc-200 rounded-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkle size={18} className="text-[#002FA7]" />
            <h2 className="font-heading text-lg font-semibold">Playground</h2>
          </div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Firmenname zum Testen</Label>
          <Input
            value={testName}
            onChange={(e) => setTestName(e.target.value)}
            data-testid="test-name-input"
            className="mt-1.5"
            onKeyDown={(e) => e.key === "Enter" && runTest()}
          />
          <Button onClick={runTest} disabled={testing} data-testid="run-test-button" className="mt-4 bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm">
            <Play size={16} weight="fill" className="mr-2" />
            {testing ? "Teste…" : "Ableitung testen"}
          </Button>

          {testResult && (
            <div className="mt-6 space-y-3" data-testid="test-result">
              {testResult.error ? (
                <div className="text-sm text-[#E53935] bg-red-50 border border-red-200 rounded-sm p-3">
                  {testResult.error}
                </div>
              ) : (
                <>
                  <ResultRow label="KI-Ergebnis" value={testResult.ki} accent />
                  <ResultRow label="Heuristik (ohne KI)" value={testResult.heuristik} />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultRow({ label, value, accent }) {
  return (
    <div className={`rounded-sm border p-4 ${accent ? "border-[#002FA7] bg-[#002FA7]/5" : "border-zinc-200"}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-heading text-xl font-bold mt-0.5 ${accent ? "text-[#002FA7]" : ""}`}>{value}</div>
    </div>
  );
}
